import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { VStack, Box, Button, CircularProgress } from "@chakra-ui/react";
import { Browser, getBrowser, userAgentIsAndroid, userAgentIsIOS } from "utils/device";
import { userAgentIsMobile } from "utils/device";
import StepsBox from "components/StepsBox";
import { fetchProvider } from "utils/backend";
import { extractDomain, extractVariablesFromResponseMatches } from "utils/helper";
import {
  androidAppDownloadLink,
  iosAppDownloadLink,
  reclaimVerifierMobileAppUrIScheme,
} from "utils/constants";
import { GetAppModal } from "components/OpenInAppDialog";
import { updateSession } from "utils/update-session";
import { THREE_JAN_APP_ID } from "utils/constants";

// Chromium legacy browsers (< Chromium 91.0) do not handle external protocols: https://github.com/blueboxd/chromium-legacy/issues/18

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const Verify: React.FC = () => {
  const query = useQuery();
  const APP_ID = process.env.REACT_APP_PUBLIC_APP_ID || "";
  const APP_SECRET = process.env.REACT_APP_PUBLIC_APP_SECRET || "";

  const [isGetAppModalOpen, setIsGetAppModalOpen] = useState(false);

  const [appName, setAppName] = useState<string>("");
  const [appImage, setAppImage] = useState<string>("");
  const [appId, setAppId] = useState<string>("");
  const [variables, setVariables] = useState<string[]>([""]);
  const [providerHost, setProviderHost] = useState<string>("");
  const [providerLogo, setProviderLogo] = useState<string>("");
  const [updatedSession, setUpdatedSession] = useState<boolean>(false);
  const [steps, setSteps] = useState<string[]>([]);
  const browser = getBrowser();

  // Open in Safari if not Safari and on iOS
  useEffect(() => {
    if (browser !== Browser.Safari && userAgentIsIOS) {
      window.open(`x-safari-${window.location.href}`, "_blank");
    }
  }, []);

  const getAppInfo = async (appId: string) => {
    try {
      const response = await fetch(
        `https://api.reclaimprotocol.org/api/applications/info/${appId}`
      );
      const data = await response.json();
      const appName = data.application.name;
      const appImage = data.application?.appImageUrl ?? null;
      setAppName(appName);
      setAppImage(appImage);
    } catch (error) {
      console.error("Error fetching app name:", error);
    }
  };

  const fetchProviderData = async (providerId: string) => {
    const provider = await fetchProvider(providerId, APP_ID, APP_SECRET);
    const url = provider?.providers.httpProvider[0].loginUrl;
    const providerLogo = provider?.providers.httpProvider[0].logoUrl;
    const domain = extractDomain(url);
    const variables = extractVariablesFromResponseMatches(
      provider?.providers?.httpProvider[0]?.requestData[0]?.responseMatches ?? []
    );
    setVariables(variables);
    setProviderHost(domain);
    setProviderLogo(providerLogo);
    if (provider?.providers.httpProvider[0].stepsToFollow) {
      setSteps(
        provider?.providers.httpProvider[0].stepsToFollow
          .split("\n")
          .map((step: string) => step.trim())
      );
    }
  };

  useEffect(() => {
    try {
      const template = query.get("template");
      const templateObj = template ? JSON.parse(template) : null;
      const applicationId = templateObj?.applicationId;
      const providerId = templateObj?.providerId;
      getAppInfo(applicationId);
      fetchProviderData(providerId);
      setAppId(applicationId.trim());
    } catch (e) {
      console.error("Error parsing template", e);
    }
  }, []);

  useEffect(() => {
    if (updatedSession) {
      return;
    }
    const template = query.get("template");
    if (!template) return;
    const templateObj = template ? JSON.parse(template) : null;
    const sessionId = templateObj?.sessionId;
    updateSession(sessionId, "USER_INIT_VERIFICATION").then((data) => {
      if (data) {
        setUpdatedSession(true);
      }
    });
  }, [query, updatedSession]);

  const getStoreLink = () => {
    if (userAgentIsAndroid) {
      return androidAppDownloadLink;
    } else {
      return iosAppDownloadLink;
    }
  };

  const onGetAppClick = () => {
    const storeLink = getStoreLink();
    window.open(storeLink, "_blank", "noopener,noreferrer");
    // we are not sure what platform we are on, so just let user choose anyway as fallback if the previous auto-open didn't work...
    setIsGetAppModalOpen(true);
  };

  const openLinkInMobileApp = () => {
    const template = query.get("template");

    if (!template) return;

    const url = encodeURIComponent(template);
    const encodedUrl = url.replace(/[()]/g, (match) => {
      return "%" + match.charCodeAt(0).toString(16).toUpperCase();
    });
    const appUrl = `${reclaimVerifierMobileAppUrIScheme}://requestedProofs/template=${encodedUrl}`;

    window.open(appUrl, "_self", "noopener,noreferrer");
    setIsGetAppModalOpen(true);
  };

  return (
    <>
      <VStack mx="auto" spacing={5} justify={userAgentIsMobile ? "space-between" : "center"}>
        {appName !== "" && providerHost !== "" ? (
          <>
            <Box height="10px" />
            {providerLogo && appImage && (
              <Box
                maxWidth="400px"
                width="calc(100% - 24px)"
                mt={5}
                display="flex"
                justifyContent="left"
              >
                <Box
                  w={"62px"}
                  height={"62px"}
                  backgroundImage={appImage}
                  backgroundSize={"cover"}
                  backgroundPosition={"center"}
                  borderRadius={"15px"}
                  border={"5px solid #fff"}
                  zIndex={1}
                />
                <Box
                  w={"61px"}
                  height={"61px"}
                  backgroundImage={providerLogo}
                  backgroundSize={"cover"}
                  backgroundPosition={"center"}
                  borderRadius={"15px"}
                  zIndex={0}
                  marginLeft="-15px"
                />
                {/* <Image
                  src={appImage}
                  alt="App Image"
                  boxSize="41px"
                  borderRadius="12px"
                  border="1px solid white"
                  position="relative"
                  zIndex={1}
                />
                <Image
                  src={providerLogo}
                  alt="Provider Logo"
                  boxSize="41px"
                  borderRadius="12px"
                  border="1px solid white"
                  marginLeft="-4px"
                  position="relative"
                  zIndex={0}
                /> */}
              </Box>
            )}
            <Box
              maxWidth="400px"
              width="calc(100% - 24px)"
              pr={10}
              color="#0F172A"
              lineHeight="27.45px"
              fontSize="23px"
              fontWeight={500}
            >
              <strong>{appName}</strong> is requesting data from{" "}
              <span style={{ textTransform: "capitalize" }}>{providerHost}</span>
            </Box>
            <StepsBox host={providerHost} variables={variables} steps={steps} />
          </>
        ) : (
          <CircularProgress color="#007AFF" isIndeterminate pt={10} />
        )}
        {appId.toLowerCase() !== THREE_JAN_APP_ID && (
          <>
            <Button
              maxWidth="400px"
              width="calc(100% - 24px)"
              bg="#007AFF"
              borderRadius="12px"
              height="50px"
              textColor="white"
              lineHeight="22px"
              fontSize="17px"
              fontWeight={400}
              onClick={openLinkInMobileApp}
              _active={{ bg: "#007AFF" }}
              _hover={{ bg: "#007AFF" }}
            >
              Continue on App
            </Button>
            <Box
              maxWidth="400px"
              width="calc(100% - 24px)"
              color="rgba(0, 0, 0, 0.32)"
              pr={10}
              lineHeight="27.45px"
              fontSize="17px"
              fontWeight={400}
              textAlign="center"
            >
              Having trouble?{" "}
              <button
                onClick={onGetAppClick}
                style={{
                  textDecoration: "underline",
                  fontWeight: "bold",
                  border: "none",
                  background: "none",
                  padding: "0",
                }}
              >
                Install the Reclaim App
              </button>
            </Box>
          </>
        )}

        <GetAppModal isOpen={isGetAppModalOpen} onClose={() => setIsGetAppModalOpen(false)} />
      </VStack>
    </>
  );
};

export default Verify;
